<template>
  <div class="content">
    <common-header :data="['home', 'campusDrive', 'moodleCourses']">
    </common-header>

    <div class="mb-3 ml-3">
      <label for="instance-select" class="form-label mb-0 mr-2">
        <b-icon icon="server" class="mr-1" font-scale="1.2"></b-icon>
        Select Instance:
      </label>
      <select
        id="instance-select"
        class="form-control form-control-sm custom-instance-select"
        style="display: inline"
        v-model.number="localSelectedInstanceId"
      >
        <option disabled :value="null">-- Choose an instance --</option>
        <option
          v-for="instance in instances"
          :key="instance.id"
          :value="instance.id"
        >
          {{ instance.label }}
        </option>
      </select>
    </div>

    <div v-if="showCourses" class="container-fluid mt-3">
      <div class="row">
        <div
          v-for="course in courses"
          :key="course.id"
          class="col-md-4 col-sm-6 mb-3"
          style="padding-left: 0.5rem; padding-right: 0.5rem"
        >
          <div
            class="border shadow-sm d-flex flex-column"
            style="border-radius: 0.5rem"
          >
            <!-- 🔵 Card Header -->
            <div
              style="
                background-color: #f0f4ff;
                border-top-left-radius: 0.5rem;
                border-top-right-radius: 0.5rem;
              "
            >
              <div
                class="d-flex justify-content-between align-items-start px-3 py-2"
              >
                <!-- Left: Course Name + Entity -->
                <div>
                  <h5 class="mb-1">
                    <strong v-html="course.displayname"></strong>
                  </h5>
                  <router-link
                    v-if="showRecruitmentDrive(course)"
                    :to="{
                      name: 'registeredApplicantList',
                      params: { recruitmentId: course.recruitment_session_id },
                    }"
                    class="d-inline-flex align-items-center"
                    title="View recruitment drive"
                  >
                    <small class="text-dark">
                      {{ course.recruitment_session_name }}
                    </small>
                    <b-icon
                      v-if="course.recruitment_session_name"
                      icon="arrow-right"
                      style="color: #8f1452"
                    />
                  </router-link>
                  <small v-else class="text-muted">
                    No recruitment drive assigned
                  </small>
                </div>
                <!-- Right Icons -->
                <div
                  class="d-flex justify-content-between align-items-center"
                  style="gap: 0.5rem"
                >
                  <button
                    class="btn p-0"
                    title="Delete Student Users"
                    data-toggle="tooltip"
                    @click="openDeleteApplicantUserModal(course.id)"
                    style="border: none; color: #8f1452"
                  >
                    <b-icon icon="person-dash" font-scale="1.2"></b-icon>
                  </button>
                  <button
                    class="btn p-0"
                    title="Duplicate Course"
                    data-toggle="tooltip"
                    @click="openDuplicateCourseModal(course.id)"
                    style="border: none; color: #8f1452"
                  >
                    <b-icon icon="files" font-scale="1.2"></b-icon>
                  </button>
                  <button
                    class="btn p-0"
                    title="Delete Course"
                    data-toggle="tooltip"
                    @click="openDeleteCourseModal(course.id)"
                    style="border: none; color: #8f1452"
                  >
                    <b-icon icon="trash" font-scale="1.2"></b-icon>
                  </button>
                  <button
                    class="btn p-0"
                    title="Edit Configurations"
                    data-toggle="tooltip"
                    @click="openCourseModal(course.id)"
                    style="border: none; color: #8f1452"
                  >
                    <b-icon icon="gear" font-scale="1.2"></b-icon>
                  </button>
                </div>
              </div>
            </div>

            <!-- Course Content -->
            <div class="px-3 py-1">
              <small>
                Timing:- {{ formatDate(course.startdate) }} -
                {{ formatDate(course.enddate) }}
              </small>
            </div>

            <div class="d-flex justify-content-center mt-1">
              <button
                class="btn p-0 d-flex align-items-center"
                @click.stop="toggleCourse(course.id)"
                style="width: auto; font-size: 0.85rem; color: #8f1452"
              >
                <b-icon
                  :icon="isExpanded(course.id) ? 'chevron-up' : 'chevron-down'"
                  font-scale="0.85"
                  class="mr-1"
                />
                {{ isExpanded(course.id) ? "Hide Quizzes" : "Show Quizzes" }}
              </button>
            </div>

            <!-- Course Quizzes (Collapsible) -->
            <div v-if="isExpanded(course.id)" class="mt-1 mb-2 mx-3">
              <ul
                v-if="quizzesByCourse(course.id)?.length > 0"
                class="p-0 m-0 list-unstyled"
              >
                <li
                  v-for="quiz in quizzesByCourse(course.id)"
                  :key="quiz.id"
                  class="mb-2"
                >
                  <div class="py-1 px-2 quiz-card rounded bg-white">
                    <strong v-html="quiz.name"></strong>
                    <br />
                    <div class="quiz-time-row mb-2">
                      <label class="time-label">
                        Time Limit (in minutes) :-
                      </label>

                      <div class="quiz-time-controls">
                        <b-form-input
                          :readonly="!quiz.isEditingTime"
                          v-model.number="quiz.timeValue"
                          type="number"
                          min="1"
                          class="form-control-sm time-input"
                          :class="{ 'no-border': !quiz.isEditingTime }"
                          @keydown="preventInvalidKeysForTime"
                          @input="validateTimeLimit(quiz)"
                        ></b-form-input>

                        <button
                          class="btn btn-sm ml-2"
                          :class="
                            quiz.isEditingTime
                              ? 'btn-outline-primary btn-custom-color'
                              : 'btn-outline-secondary'
                          "
                          @click="toggleEditTime(course.id, quiz)"
                          style="font-size: 0.85rem; padding: 0.25rem 0.5rem"
                        >
                          {{ quiz.isEditingTime ? "Save" : "Edit" }}
                        </button>
                      </div>
                    </div>

                    <small v-if="quiz.timeLimitError" class="text-danger">
                      {{ quiz.timeLimitError }}
                    </small>
                  </div>
                </li>
              </ul>
              <div
                v-else-if="quizzesByCourse(course.id)?.length === 0"
                class="text-muted text-center d-flex justify-content-center align-items-center"
              >
                <b-icon
                  icon="exclamation-circle"
                  font-scale="1.2"
                  class="mr-2"
                ></b-icon>
                <p class="mb-0">No quizzes available in this course.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else-if="this.rawCourses && this.rawCourses.length == 0"
      class="text-center my-5 text-muted"
    >
      <b-icon icon="journal-x" variant="secondary" font-scale="4"></b-icon>
      <h4 class="mt-3">No courses available</h4>
      <p>
        Please select a different instance or add a course in this instance.
      </p>
    </div>

    <course-modal
      ref="courseModal"
      :course-id="selectedCourseId"
      :instance-id="selectedInstanceId === '' ? null : selectedInstanceId"
    ></course-modal>

    <duplicate-course-modal
      ref="duplicateCourseModal"
      :course-id="selectedCourseId"
      :instance-id="selectedInstanceId === '' ? null : selectedInstanceId"
    ></duplicate-course-modal>

    <confirmation-modal
      ref="deleteApplicantUserModal"
      modal-id="confirm-delete-applicant-user-modal"
      modal-title="Confirm Delete Applicant Users"
      modal-message="Are you sure you want to delete the applicant users from this course?"
      :action="deleteUsersFromCourse"
    />

    <confirmation-modal
      ref="deleteCourseModal"
      modal-id="confirm-delete-course-modal"
      modal-title="Confirm Delete Course"
      modal-message="Are you sure you want to delete this course from moodle?"
      :action="deleteCourse"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import courseModal from "./course-modal.vue";
import duplicateCourseModal from "./duplicate-course-modal.vue";
import dayjs from "dayjs";
import confirmationModal from "../../Common/_components/confirmation-modal.vue";

export default {
  components: { courseModal, duplicateCourseModal, confirmationModal },
  data() {
    return {
      expandedCourseId: null,
      selectedCourseId: null,
      selectedQuizId: null,
      selectedQuizName: null,
      localSelectedInstanceId: null,
    };
  },
  computed: {
    ...mapGetters({
      rawCourses: "$_moodle_course/getCourses",
      quizzesByCourse: "$_moodle_course/getQuizzesByCourse",
      instances: "$_applicant_profile/fetchMoodleInstanceList",
      selectedInstanceId: "$_moodle_course/getSelectedInstanceId",
    }),
    showCourses() {
      return this.courses && this.courses.length > 0;
    },
    courses() {
      return [...(this.rawCourses || [])].sort(
        (a, b) => b.timecreated - a.timecreated
      ); // Descending (newest first)
    },
  },
  watch: {
    localSelectedInstanceId(newVal) {
      this.setSelectedInstanceId(newVal);
      this.fetchCourses();
    },
  },
  methods: {
    ...mapMutations({
      setSelectedInstanceId: "$_moodle_course/SET_SELECTED_INSTANCE_ID",
    }),
    openDeleteApplicantUserModal(courseId) {
      this.selectedCourseId = courseId;
      this.$refs.deleteApplicantUserModal.$refs.confirmationModal.show();
    },
    showRecruitmentDrive(course) {
      return (
        course.recruitment_session_name &&
        new Date(course.enddate * 1000) >= new Date()
      );
    },
    onInstanceChange(event) {
      const value = event.target.value;
      this.setSelectedInstanceId(Number(value));
      this.fetchCourses();
    },
    formatDate(timestamp) {
      return dayjs.unix(timestamp).format("ddd, DD MMM YYYY [at] hh:mm A");
    },
    formatTime(seconds) {
      if (seconds % 3600 === 0) {
        return seconds / 3600 + " hours";
      } else if (seconds % 60 === 0) {
        return seconds / 60 + " minutes";
      } else {
        return seconds + " seconds";
      }
    },
    fetchCourses() {
      this.expandedCourseId = null; // collapse any open course
      this.$store.dispatch(
        "$_moodle_course/fetchCourses",
        this.selectedInstanceId
      );
    },
    fetchInstances() {
      this.$store.dispatch("$_applicant_profile/getMoodleInstanceList");
    },
    toggleCourse(courseId) {
      if (this.expandedCourseId == courseId) this.expandedCourseId = null;
      else {
        this.expandedCourseId = courseId;
        this.$store.dispatch("$_moodle_course/fetchQuizzesByCourseId", {
          instanceId: this.selectedInstanceId,
          courseId,
        });
      }
    },
    isExpanded(courseId) {
      return this.expandedCourseId == courseId;
    },
    openCourseModal(courseId) {
      this.$store.dispatch("$_moodle_course/fetchQuizzesByCourseId", {
        instanceId: this.selectedInstanceId,
        courseId,
      });
      this.$store.dispatch("$_moodle_course/fetchCourseConfigurations", {
        instanceId: this.selectedInstanceId,
        courseId,
      });
      this.selectedCourseId = courseId;
      this.$refs.courseModal.show();
    },
    openDuplicateCourseModal(courseId) {
      this.$store.dispatch(
        "$_moodle_course/fetchCategories",
        this.selectedInstanceId
      );
      this.selectedCourseId = courseId;
      this.$refs.duplicateCourseModal.show();
    },
    deleteUsersFromCourse() {
      const courseId = this.selectedCourseId;
      this.selectedCourseId = null;
      this.$store.dispatch("$_moodle_course/deleteUsersFromCourse", {
        instanceId: this.selectedInstanceId,
        courseId,
      });
    },
    openDeleteCourseModal(courseId) {
      this.selectedCourseId = courseId;
      this.$refs.deleteCourseModal.$refs.confirmationModal.show();
    },
    deleteCourse() {
      const courseIds = [this.selectedCourseId];
      this.selectedCourseId = null;
      this.$store.dispatch("$_moodle_course/deleteCourse", {
        instanceId: this.selectedInstanceId,
        courseIds,
      });
    },
    preventInvalidKeysForTime(event) {
      const input = event.target;
      const key = event.key;

      const invalidKeys = ["e", "E", "+", "-", "."];
      if (invalidKeys.includes(key)) {
        event.preventDefault();
      }

      // Prevent '0' as the first character
      if (
        key === "0" &&
        (input.value.length === 0 ||
          (input.selectionStart === 0 && !input.value.length))
      ) {
        event.preventDefault();
      }
    },
    validateTimeLimit(quiz) {
      if (!quiz.timeValue || quiz.timeValue <= 0) {
        quiz.timeLimitError = "Time must be greater than 0.";
      } else {
        quiz.timeLimitError = null;
        quiz.timelimit = quiz.timeValue * 60;
      }
    },
    updateQuizTimeLimit(courseId, quiz) {
      this.$store.dispatch("$_moodle_course/updateQuizTimelimitByQuizId", {
        courseId,
        instanceId: this.selectedInstanceId,
        quizId: quiz.id,
        timelimit: quiz.timelimit,
      });
    },
    toggleEditTime(courseId, quiz) {
      if (quiz.isEditingTime) this.updateQuizTimeLimit(courseId, quiz);
      quiz.isEditingTime = !quiz.isEditingTime;
    },
  },
  mounted() {
    this.fetchInstances();
    this.$store.commit("$_moodle_course/SET_COURSES", []);
    this.localSelectedInstanceId = this.selectedInstanceId;
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.custom-instance-select {
  font-size: 1rem;
  max-width: 200px;
  border-radius: 4px;
}

label.form-label {
  font-weight: 500;
  color: #333;
}

.btn-custom-color {
  color: #8f1452 !important;
  border-color: #8f1452 !important;
}

.btn-custom-color:hover {
  background-color: #8f1452 !important;
  color: white !important;
}

.time-input {
  max-width: 4rem;
  min-width: 3rem; /* it doesn't shrink too small */
}

.no-border {
  border: none !important;
  box-shadow: none !important;
  pointer-events: none; /* disables cursor interactions when read-only */
}

.quiz-card {
  background-color: #fef5f9;
  transition: box-shadow 0.2s ease-in-out;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  border: none;
  border-radius: 0.5rem;
}

.quiz-card:hover {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.18);
}

.course-content {
  background-color: #f9fafb;
  padding: 0.5rem 1rem;
}

.quiz-time-row {
  display: flex;
  flex-direction: column;
}

.time-label {
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.quiz-time-controls {
  display: flex;
  flex-direction: row;
  align-items: center;
}

/* Custom breakpoints */
@media (min-width: 340px) and (max-width: 575px),
  (min-width: 687px) and (max-width: 767px),
  (min-width: 1035px) {
  .quiz-time-row {
    flex-direction: row;
    align-items: center;
  }

  .time-label {
    min-width: 160px;
    margin-bottom: 0;
    margin-right: 0.5rem;
  }

  .quiz-time-controls {
    flex-direction: row;
    align-items: center;
  }
}
</style>
